/**
 * WL-K3 — ClientDatevConfigPanel: the Kanzlei form to maintain a mandant's DATEV
 * master data (Berater-/Mandanten-Nr., Kontenrahmen, Sachkontenlänge) plus an
 * „exportbereit?"-Ampel.
 *
 * SICHERHEIT: this component is purely cosmetic. Every hard check (Loop-B-Beta +
 * active advisor membership + the field whitelist) is enforced server-side in the
 * `setClientDatevConfig`/`getClientDatevConfig` callables. A non-beta or
 * non-advisor caller receives a uniform `permission-denied` from the server; the
 * form never bypasses the mandant's owner-write-only `sellers` rules — the write
 * runs server-side via the Admin SDK.
 *
 * The parent (AdvisorCockpitPage) gates visibility on `canUseKanzleiCockpit`
 * (page-level) AND additionally on `canUseAdvisorDatev` for this DATEV panel
 * (M-GA-4 🔴 PFLICHTPUNKT 2 — a Cockpit-GA flip must not leak the still-Beta
 * DATEV surface); it re-loads whenever the selected mandant changes.
 *
 * @module components/tenant/ClientDatevConfigPanel
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSuccess, IconWarning } from '../../icons';
import {
  getClientDatevConfig,
  setClientDatevConfig,
  type ClientDatevConfigInput,
} from '../../api/clientDatevApi';
import {
  DATEV_SKR_VALUES,
  DATEV_SACHKONTEN_LAENGE_MIN,
  DATEV_SACHKONTEN_LAENGE_MAX,
  isValidBeraterNr,
  isValidMandantenNr,
  isValidSachkontenLaenge,
  isExportReady,
  type ClientDatevConfigView,
  type DatevSkr,
} from '../../utils/clientDatevFormat';

/** Editable string-backed form state (numbers held as strings for the inputs). */
interface FormState {
  datevBeraterNr: string;
  datevMandantenNr: string;
  datevSkr: '' | DatevSkr;
  datevSachkontenLaenge: string;
}

/** Maps a loaded server config into editable form strings. */
function toForm(config: ClientDatevConfigView): FormState {
  return {
    datevBeraterNr: config.datevBeraterNr ?? '',
    datevMandantenNr: config.datevMandantenNr ?? '',
    datevSkr: config.datevSkr ?? '',
    datevSachkontenLaenge:
      config.datevSachkontenLaenge === null ? '' : String(config.datevSachkontenLaenge),
  };
}

/** Maps the editable form back to the current effective config (for the Ampel). */
function toConfigView(form: FormState): ClientDatevConfigView {
  const laenge = Number.parseInt(form.datevSachkontenLaenge, 10);
  return {
    datevBeraterNr: form.datevBeraterNr.trim() || null,
    datevMandantenNr: form.datevMandantenNr.trim() || null,
    datevSkr: form.datevSkr === '' ? null : form.datevSkr,
    datevSachkontenLaenge: Number.isNaN(laenge) ? null : laenge,
  };
}

/**
 * Builds the callable payload: only non-empty, VALID fields are sent so the server
 * never rejects the whole request over a partially typed field. An empty field is
 * omitted (leaves the stored value untouched).
 */
function toPayload(form: FormState): ClientDatevConfigInput {
  const payload: ClientDatevConfigInput = {};
  const berater = form.datevBeraterNr.trim();
  if (berater && isValidBeraterNr(berater)) payload.datevBeraterNr = berater;
  const mandant = form.datevMandantenNr.trim();
  if (mandant && isValidMandantenNr(mandant)) payload.datevMandantenNr = mandant;
  if (form.datevSkr !== '') payload.datevSkr = form.datevSkr;
  const laenge = Number.parseInt(form.datevSachkontenLaenge, 10);
  if (!Number.isNaN(laenge) && isValidSachkontenLaenge(laenge)) {
    payload.datevSachkontenLaenge = laenge;
  }
  return payload;
}

type Status =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'saveError' };

/** Per-mandant DATEV master-data form. */
const ClientDatevConfigPanel: React.FC<{ mandantUid: string }> = ({ mandantUid }) => {
  const { t } = useTranslation('common');
  const [form, setForm] = useState<FormState>({
    datevBeraterNr: '',
    datevMandantenNr: '',
    datevSkr: '',
    datevSachkontenLaenge: '',
  });
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setStatus({ kind: 'loading' });
    getClientDatevConfig(mandantUid)
      .then((res) => {
        if (cancelled) return;
        setForm(toForm(res.config));
        setStatus({ kind: 'ready' });
      })
      .catch(() => {
        if (!cancelled) setStatus({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [mandantUid]);

  const update = (patch: Partial<FormState>): void => {
    setForm((prev) => ({ ...prev, ...patch }));
    // Any edit clears a prior saved/error banner back to the neutral ready state.
    setStatus((prev) =>
      prev.kind === 'saved' || prev.kind === 'saveError' ? { kind: 'ready' } : prev,
    );
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setStatus({ kind: 'saving' });
    try {
      await setClientDatevConfig(mandantUid, toPayload(form));
      setStatus({ kind: 'saved' });
    } catch {
      setStatus({ kind: 'saveError' });
    }
  };

  if (status.kind === 'loading') {
    return (
      <section className="rounded-lg bg-surface-raised p-5">
        <p className="text-sm text-muted">{t('clientDatev.loading')}</p>
      </section>
    );
  }
  if (status.kind === 'error') {
    return (
      <section className="rounded-lg bg-surface-raised p-5">
        <p className="text-sm text-[var(--tone-danger-fg)]">{t('clientDatev.loadError')}</p>
      </section>
    );
  }

  const configView = toConfigView(form);
  const ready = isExportReady(configView);
  const beraterInvalid =
    form.datevBeraterNr.trim() !== '' && !isValidBeraterNr(form.datevBeraterNr);
  const mandantInvalid =
    form.datevMandantenNr.trim() !== '' && !isValidMandantenNr(form.datevMandantenNr);
  const laengeNum = Number.parseInt(form.datevSachkontenLaenge, 10);
  const laengeInvalid =
    form.datevSachkontenLaenge.trim() !== '' &&
    (Number.isNaN(laengeNum) || !isValidSachkontenLaenge(laengeNum));

  return (
    <section className="rounded-lg bg-surface-raised p-5">
      <header className="mb-4">
        <h2 className="text-base font-semibold text-primary">{t('clientDatev.heading')}</h2>
        <p className="mt-1 text-xs text-muted">{t('clientDatev.subheading')}</p>
      </header>

      {/* „exportbereit?"-Ampel */}
      <div
        className={`mb-4 flex items-start gap-2 rounded-md p-3 text-sm ${
          ready ? 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)]' : 'bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)]'
        }`}
        role="status"
      >
        {ready ? (
          <IconSuccess size={16} className="mt-0.5 shrink-0 text-[var(--tone-success-fg)]" />
        ) : (
          <IconWarning size={16} className="mt-0.5 shrink-0 text-[var(--tone-warning-fg)]" />
        )}
        <div>
          <div className="font-semibold">
            {ready ? t('clientDatev.readyTitle') : t('clientDatev.notReadyTitle')}
          </div>
          <div className="text-xs opacity-90">
            {ready ? t('clientDatev.readyBody') : t('clientDatev.notReadyBody')}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary">
              {t('clientDatev.beraterNrLabel')}
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={form.datevBeraterNr}
              onChange={(e) => update({ datevBeraterNr: e.target.value })}
              className="w-full rounded-md border border-strong bg-surface px-3 py-2 text-sm text-primary focus:border-indigo-400 focus:outline-none"
            />
            <span className={`mt-1 block text-xs ${beraterInvalid ? 'text-[var(--tone-danger-fg)]' : 'text-faint'}`}>
              {beraterInvalid ? t('clientDatev.beraterNrInvalid') : t('clientDatev.beraterNrHint')}
            </span>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary">
              {t('clientDatev.mandantenNrLabel')}
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={form.datevMandantenNr}
              onChange={(e) => update({ datevMandantenNr: e.target.value })}
              className="w-full rounded-md border border-strong bg-surface px-3 py-2 text-sm text-primary focus:border-indigo-400 focus:outline-none"
            />
            <span className={`mt-1 block text-xs ${mandantInvalid ? 'text-[var(--tone-danger-fg)]' : 'text-faint'}`}>
              {mandantInvalid ? t('clientDatev.mandantenNrInvalid') : t('clientDatev.mandantenNrHint')}
            </span>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary">
              {t('clientDatev.skrLabel')}
            </span>
            <select
              value={form.datevSkr}
              onChange={(e) => update({ datevSkr: e.target.value as '' | DatevSkr })}
              className="w-full rounded-md border border-strong bg-surface px-3 py-2 text-sm text-primary focus:border-indigo-400 focus:outline-none"
            >
              <option value="">{t('clientDatev.skrPlaceholder')}</option>
              {DATEV_SKR_VALUES.map((skr) => (
                <option key={skr} value={skr}>
                  {skr}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary">
              {t('clientDatev.sachkontenLaengeLabel')}
            </span>
            <select
              value={form.datevSachkontenLaenge}
              onChange={(e) => update({ datevSachkontenLaenge: e.target.value })}
              className="w-full rounded-md border border-strong bg-surface px-3 py-2 text-sm text-primary focus:border-indigo-400 focus:outline-none"
            >
              <option value="">—</option>
              {Array.from(
                { length: DATEV_SACHKONTEN_LAENGE_MAX - DATEV_SACHKONTEN_LAENGE_MIN + 1 },
                (_, i) => DATEV_SACHKONTEN_LAENGE_MIN + i,
              ).map((len) => (
                <option key={len} value={String(len)}>
                  {len}
                </option>
              ))}
            </select>
            <span className={`mt-1 block text-xs ${laengeInvalid ? 'text-[var(--tone-danger-fg)]' : 'text-faint'}`}>
              {laengeInvalid
                ? t('clientDatev.sachkontenLaengeInvalid')
                : t('clientDatev.sachkontenLaengeHint')}
            </span>
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={status.kind === 'saving'}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status.kind === 'saving' ? t('clientDatev.saving') : t('clientDatev.save')}
          </button>
          {status.kind === 'saved' && (
            <span className="text-sm text-[var(--tone-success-fg)]">{t('clientDatev.saved')}</span>
          )}
          {status.kind === 'saveError' && (
            <span className="text-sm text-[var(--tone-danger-fg)]">{t('clientDatev.saveError')}</span>
          )}
        </div>
      </form>

      <p className="mt-4 text-xs leading-relaxed text-faint">{t('clientDatev.hint')}</p>
    </section>
  );
};

export default ClientDatevConfigPanel;
